// Global stats marquee — rendered by App.js immediately below <NavBar />.
// Polls /api/stats/public every 45 s and rotates through five metrics as a
// single thin scrolling line. Hidden on the same 4 gameplay routes that
// NavBar is hidden on (delegated by App.js — this component itself does
// not know about routes).
import { useEffect, useState } from "react";
import axios from "axios";
import "./TickerBar.css";

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 45_000;

const MODE_LABELS = {
  classic: "Classic",
  extended_classic: "Extended Classic",
  tmc: "Two-Move Chess",
  extended_tmc: "Extended Two-Move",
  ctc: "Capture the Crown",
  br: "Battle Royale",
  lok: "Legend of Kings",
  ctk: "Capture the King",
  fpc: "Four-Player Chess",
  fpc_team: "2v2 Team Chess",
  faction_wars: "Faction Wars",
};

const FACTION_LABELS = {
  "iron-kingdom":   "Iron Kingdom",
  "dragonborn":     "Dragonborn",
  "elf-court":      "Elf Court",
  "celestial-host": "Celestial Host",
};

function buildMessages(s) {
  if (!s) return [];
  const msgs = [];
  msgs.push(`⚔  ${s.games_in_progress} game${s.games_in_progress === 1 ? "" : "s"} in progress right now`);
  msgs.push(`🏁  ${s.games_finished_today} game${s.games_finished_today === 1 ? "" : "s"} finished today`);
  if (s.top_mode_24h && s.top_mode_24h._id) {
    const label = MODE_LABELS[s.top_mode_24h._id] || s.top_mode_24h._id;
    msgs.push(`🔥  Most played (24h): ${label} — ${s.top_mode_24h.n} matches`);
  }
  const shares = Object.entries(s.faction_share || {});
  if (shares.length) {
    shares.sort((a, b) => b[1] - a[1]);
    const parts = shares.map(([f, pct]) => `${FACTION_LABELS[f] || f} ${pct}%`);
    msgs.push(`🛡  Faction pledges: ${parts.join(" · ")}`);
  }
  if (s.top_players && s.top_players.length) {
    const parts = s.top_players.map(
      (p, i) => `${i + 1}. ${p.name} (${p.rating})`
    );
    msgs.push(`👑  Top rated: ${parts.join(" · ")}`);
  }
  return msgs;
}

export default function TickerBar() {
  const [stats, setStats] = useState(null);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const fetchStats = async () => {
      try {
        const { data } = await axios.get(`${API}/api/stats/public`, {
          timeout: 8000,
        });
        if (!cancelled) setStats(data);
      } catch {
        /* silent — ticker is decorative, not critical */
      }
    };
    fetchStats();
    const iv = setInterval(fetchStats, POLL_MS);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  const messages = buildMessages(stats);

  // Rotate through messages every 6 s once we have any.
  useEffect(() => {
    if (messages.length <= 1) return;
    const iv = setInterval(() => {
      setIdx((i) => (i + 1) % messages.length);
    }, 6000);
    return () => clearInterval(iv);
  }, [messages.length]);

  if (!messages.length) return null;
  const current = messages[idx % messages.length];

  return (
    <div className="ticker-root" data-testid="global-ticker">
      <div className="ticker-track" key={idx /* re-triggers marquee animation */}>
        <span className="ticker-msg">{current}</span>
      </div>
    </div>
  );
}
